/**
 * @license
 * Copyright (c) 2008 %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Point } from './LineUtils';
import { curveThroughPoints } from './PathUtils';

/**
 * Returns a straight area path command.
 * @param {array} lineSegmentCoords Polyline top points array.
 * @param {array} bottomCoords Polyline bottom points array.
 * @return {string} Straight area path commands.
 */
export function getStraightAreaPath(lineSegmentCoords: Point[], bottomCoords: Point[]) {
  // Create the path data string
  let pathData;
  if (lineSegmentCoords.length > 0) {
    pathData =
      lineSegmentCoords
        .map((lineSegmentCoord, index) => {
          return `${index === 0 ? 'M' : 'L'} ${lineSegmentCoord[0]} ${lineSegmentCoord[1]}`;
        })
        .join(' ') +
      [...bottomCoords]
        .reverse()
        .map((bottomCoord) => {
          return `L ${bottomCoord[0]} ${bottomCoord[1]}`;
        })
        .join(' ') +
      'Z';
  }
  return pathData;
}

/**
 * Returns a curved area path command, based on cubic hermite splines, that goes through the points in the points array.
 * @param {array} lineSegmentCoords Polyline top points array.
 * @param {array} bottomCoords Polyline bottom points array.
 * @param {boolean} connectWithLine Whether the first point is reached using lineTo. Otherwise, moveTo is used.
 * @param {string} splineType The spline type.
 * @return {string} Curved area path commands.
 */
export function getCurveAreaPath(
  lineSegmentCoords: Point[],
  bottomCoords: Point[],
  connectWithLine: boolean,
  splineType: string,
  isBottomSegmentCurved?: boolean
) {
  let pathData;
  if (lineSegmentCoords.length > 0) {
    if (!isBottomSegmentCurved) {
      pathData =
        curveThroughPoints(lineSegmentCoords, connectWithLine, splineType) +
        [...bottomCoords]
          .reverse()
          .map((bottomCoord) => {
            return `L ${bottomCoord[0]} ${bottomCoord[1]}`;
          })
          .join(' ') +
        'Z';
    } else {
      pathData =
        curveThroughPoints(lineSegmentCoords, false, splineType) +
        ' ' +
        curveThroughPoints([...bottomCoords].reverse(), true, splineType) +
        ' Z';
    }
  }
  return pathData;
}
